import { DateTime } from 'luxon'

// These are endpoint used to call F-EDGE service

export const SAIL_CHANNEL = 'ONLINE'

export type CreateType = 'customer' | 'company'

type AuthSource = 'smartId' | 'mobileId' | 'webEid'

interface ReservationIdParams {
  reservationId: string
}

type ReservationModificationAction = 'start' | 'cancel' | 'finish' | 'modify'

interface TicketPreviewParams extends ReservationIdParams {
  format: 'PDF'
  locale: string
  reservationToken: string
}

export type SailPackageAndDateParams = { sailPackage: string; date: string; toDate?: string }
type SailRefIdAndRoundTripParams = { sailRefId: string; roundTrip: string }
type CompanyIdAndCustomerIdParams = { companyId: string; customersIds: string }
type CompanyIdParams = { companyId: string }
type CompanyIdAndTokenParams = { companyId: string; token: string }
type CompanyIdAndSingleCustomerIdParams = { companyId: string; customerId: string }

interface ReservationIdAndSailRefIdParams extends ReservationIdParams {
  sailRefId: string
}

export interface ResidentPriceCategoryParams {
  route: string
  residentId: string
  departureDate: string
}

export const getSailDatesPath = ({ sailPackage, date, toDate }: SailPackageAndDateParams) => {
  const DEFAULT_MONTHS = 12 // 1 year
  const defaultToDate = DateTime.fromISO(date).plus({ months: DEFAULT_MONTHS }).toISODate() ?? date

  return `/api/v1/sails/sailPackage/${sailPackage}/dates?fromDate=${date}&toDate=${toDate ?? defaultToDate}`
}

export const getInventoryPath = ({ sailPackage, date }: SailPackageAndDateParams) =>
  `/api/v2/inventory/saleChannel/online/sailPackage/${sailPackage}/date/${date}`

export const getSailsPath = () => '/api/v1/sails/find?offset=0&limit=100'

export const getPricesPath = ({ sailRefId, roundTrip }: SailRefIdAndRoundTripParams) =>
  `/api/v1/price/sail/${sailRefId}?roundTrip=${roundTrip}`

export const getSignInByEmailOrPhonePath = () => `/api/v1/security/token/email`

export const getSignInEmailPath = () => `/api/v1/authentication/signin`

export const getSignUpEmailPath = () => `/api/v1/authentication/signup`

// v1 is deprecated now. All new tenants should use v2. See ticket doHIHcDl
export const getCompanyInvitationLoginPath = () => '/api/v2/company/invitation/login'

export const getCompanyInvitationCheckPath = ({ token }: { token: string }) =>
  `/api/v1/company/invitation/check/${token}`

export const getActivateWithEmailPath = (hash: string) => `/login/api/v1/user/secured/activate/${hash}`

// f-edge passes this to login, which validates the Web eID auth token (trusted CAs, revocation, signature).
export const getWebEidPerformPath = () => '/api/v1/authentication/webEid/perform'

export const getPasswordResetPreparePath = () => `/api/v1/authentication/password/reset/prepare`

export const getPasswordResetValidatePath = () => `/api/v1/authentication/password/reset/validate`

export const getPasswordResetPerformPath = () => `/api/v1/authentication/password/reset/perform`

export const getCountryCatalogPath = () => `/api/v1/catalog/country`

export const getConfigPath = () => `/api/v1/config`

export const getTenant = () => `/api/v1/tenant`

export const getRoutesPath = () => `/api/v1/routes`

export const getCallingCodesCatalogPath = () => `/api/v1/catalog/COUNTRY_CALLING_CODES`

export const getSailPackagesCompactPath = () => `/api/v1/sailPackages/${SAIL_CHANNEL}/compact`

export const getPaymentMethods = ({ reservationId }: ReservationIdParams) =>
  `/api/v2/reservation/${reservationId}/paymentMethods`

export const getReservationPath = ({ reservationId }: ReservationIdParams) => `/api/v1/reservation/${reservationId}`

export const getReservationRefundOfferPath = ({ reservationId }: ReservationIdParams) =>
  `/api/v2/reservation/${reservationId}/refund/offer`

export const getReservationModificationPath = ({
  reservationId,
  action,
}: ReservationIdParams & { action: ReservationModificationAction }) =>
  `/api/v2/reservation/${reservationId}/modification/${action}`

export const getReservationProlongPath = ({ reservationId }: ReservationIdParams) =>
  `/api/v2/reservation/prolong/${reservationId}`

export const getTransactionPath = ({ reservationId }: ReservationIdParams) =>
  `/payment/api/v1/reservation/${reservationId}/transaction`

export const getTicketPreviewPath = ({ reservationId, format, locale, reservationToken }: TicketPreviewParams) =>
  `/document/api/v1/reservation/${reservationId}/confirmation/preview?format=${format}&locale=${locale}&token=${reservationToken}`

export const getReservationRefundSailsPath = ({ reservationId }: ReservationIdParams) =>
  `/api/v2/reservation/${reservationId}/refund/sails`

export const getReservationOwnerPath = ({ reservationId }: ReservationIdParams) =>
  `/api/v2/reservation/${reservationId}/owner`

export const getReservationTicketQrPath = ({ reservationId, sailRefId }: ReservationIdAndSailRefIdParams) =>
  `/api/v1/ticket/reservation/${reservationId}/sail/${sailRefId}/qr`

export const getV3ReservationPath = () => '/api/v3/reservation'

export const getV3ReservationByIdPath = ({ reservationId }: ReservationIdParams) =>
  `/api/v3/reservation/${reservationId}`

export const getV3ReservationConfirmPath = ({ reservationId }: ReservationIdParams) =>
  `/api/v3/reservation/${reservationId}/confirm`

export const secured = {
  getReservationsForUserPath: () => `/secured/api/v1/reservations?offset=0&limit=300`,
  getReservationsByManagerPath: () => '/secured/api/v1/all/reservations/by/manager',
  getManagedReservationsPath: () => '/secured/api/v1/find/reservations/managed',
  getCustomersPath: () => `/secured/api/v1/customers`,
  getCompaniesByUserPath: () => `/secured/api/v1/companies/by/user`,
  getCurrentCustomerInfoPath: () => `/secured/api/v1/customer/user/current`,
  getUserDetailsPath: () => `/secured/api/v1/user/info`,
  updateUserPath: () => `/secured/api/v1/user`,
  getCreateCustomerWithTypePath: ({ type }: { type: CreateType }) => `/secured/api/v1/${type}`,
  getDeleteCompanyOrCustomerPath: ({ type, id }: { type: CreateType; id: string }) => `/secured/api/v1/${type}/${id}`,
  getCompanyMembersPath: ({ companyId }: CompanyIdParams) => `/secured/api/v1/company/${companyId}/members`,
  getCompanyMemberInvitePath: ({ companyId }: CompanyIdParams) => `/secured/api/v1/company/${companyId}/member/invite`,
  getCompanyCustomerPath: ({ companyId, customerId }: CompanyIdAndSingleCustomerIdParams) =>
    `/secured/api/v1/company/${companyId}/customer/${customerId}`,
  getCompanyCustomerStatusPath: ({ companyId, customerId }: CompanyIdAndSingleCustomerIdParams) =>
    `/secured/api/v1/company/${companyId}/customer/${customerId}/status`,
  getCompanyInvitationPath: ({ companyId, token }: CompanyIdAndTokenParams) =>
    `/secured/api/v1/company/${companyId}/invitation/${token}`,
  getCompanyCustomersPath: ({ companyId, customersIds }: CompanyIdAndCustomerIdParams) =>
    `/secured/api/v1/company/${companyId}/customers/${customersIds}`,
  getAddCompanyMemberPath: () => `/secured/api/v1/customer/add/company/member`,
  getLogoutPath: () => `/secured/api/v1/user/logout`,
}

export const getResidentPriceCategoryPath = ({ route, residentId, departureDate }: ResidentPriceCategoryParams) =>
  `/reservation/api/v2/route/${route}/resident/${residentId}/priceCategory?departureDate=${departureDate}`

export interface VehicleLookupPathParams {
  plate: string
  sailRefId: number
  reservationId: string
  uiFormType: 'VEHICLE' | 'TRAILER'
  route: string
  isHandicapped?: boolean
  companyRegistrationNumber?: string
}

export const getVehicleLookupPath = ({
  plate,
  sailRefId,
  reservationId,
  uiFormType,
  route,
  isHandicapped,
  companyRegistrationNumber,
}: VehicleLookupPathParams) =>
  `/api/v1/vehicle/${encodeURIComponent(plate)}/sail/${sailRefId}?reservationId=${reservationId}&uiFormType=${uiFormType}&route=${encodeURIComponent(route)}&useModificationReservation=true${isHandicapped === true ? '&isHandicapped=true' : ''}${
    companyRegistrationNumber === undefined || companyRegistrationNumber === ''
      ? ''
      : `&companyRegistrationNumber=${encodeURIComponent(companyRegistrationNumber)}`
  }`

export interface VehiclePriceCategoryPathParams {
  plate: string
  sailRefId: number
  priceCategory: string
  reservationId: string
}

export const getVehiclePriceCategoryPath = ({
  plate,
  sailRefId,
  priceCategory,
  reservationId,
}: VehiclePriceCategoryPathParams) =>
  `/reservation/api/v1/vehicle/${encodeURIComponent(plate)}/sail/${sailRefId}/priceCategory/${encodeURIComponent(priceCategory)}?reservationId=${reservationId}`

export const getSmartIdPreparePath = (source: AuthSource) => `/api/v1/authentication/${source}/prepare`

export const getSmartIdPerformPath = (source: AuthSource) => `/api/v1/authentication/${source}/perform`

export const getSmartIdSessionStatusPath = (sessionId: string) =>
  `/api/v1/authentication/smartid/session/${sessionId}/status`
